import { IWorker, PrepareSignResult, PerformanceResults, PerformanceItem, VerificationPerformanceResults } from "./worker-interface";
import {
  PdfDigitalSigner,
  SignDigitalParameters,
  SignerSettings,
  SignatureEmbeder,
  SignatureChecker,
  SignatureVerifySignatureResult,
} from "sign-pdf-lib";
import * as pkijs from "pkijs";

import * as forge from "node-forge";
import { _debugOnConsole } from "./utils";
import { Verifier } from "./verifier";

/**
 * Classe com as operações de assinatura e verificação Executadas pelo Web Worker.
 * */
export class WorkerExecutor implements IWorker {
  private async createRsaKeyPair(RSA_KEY_SIZE: number): Promise<CryptoKeyPair> {
    return self.crypto.subtle.generateKey(
      {
        name: "RSASSA-PKCS1-v1_5",
        hash: "SHA-384",
        modulusLength: RSA_KEY_SIZE,
        publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
      },
      true,
      ["sign", "verify"]
    );
  }

  private async addPlaceholder(pdf: Buffer, signatureLength: number, signingTime: Date): Promise<Buffer> {
    const signerSettings: SignerSettings = {
      signatureLength,
      rangePlaceHolder: 10000000000000, // número colocado no ByteRange como placeholder. Colocamos 1tb para garantir que nunca será pequeno demais.
    };

    const signer = new PdfDigitalSigner(signerSettings);

    const signatureCount = await signer.getSignatureCount(pdf);
    const signParameters: SignDigitalParameters = {
      pageNumber: 1,
      name: `Assinatura ${signatureCount + 1}`, // mesmo nome do `document-signer`
      signature: {
        date: signingTime,
      },
    };
    return signer.addPlaceholderAsync(pdf, signParameters);
  }

  private async createCsr(keys: CryptoKeyPair): Promise<string> {
    // Curiosidade. A única coisa que o PKI usa da CSR é a chave pública, então simplesmente geramos uma CSR vazia aqui.
    const csr = new pkijs.CertificationRequest();

    await csr.subjectPublicKeyInfo.importKey(keys.publicKey);

    csr.attributes = [];
    csr.attributes.push(
      new pkijs.Attribute({
        type: "1.2.840.113549.1.9.14", // pkcs-9-at-extensionRequest
      })
    );
    await csr.sign(keys.privateKey, "SHA-256");

    const csrContent = self.btoa(String.fromCharCode(...new Uint8Array(csr.toSchema(true).toBER())));
    return `-----BEGIN CERTIFICATE REQUEST-----\n${csrContent}\n-----END CERTIFICATE REQUEST-----`;
  }

  async prepareSign(
    pdf: Buffer,
    hashAlgorithm: string,
    signatureLength: number,
    signingTime: number,
    RSA_KEY_SIZE: number,
    perf: PerformanceResults
  ): Promise<PrepareSignResult> {
    try {
      _debugOnConsole("Preparando assinatura de tamanho %s ", signatureLength);
      _debugOnConsole("Signing time: %s", signingTime);
      perf.placeholder.value = performance.now();

      // intentionally using await here to reduce precision errors in perfromance
      // measurements caused by async.
      const placeholderAndHashValuePromise = await this.addPlaceholder(pdf, signatureLength, new Date(signingTime)).then(
        async (pdfWithPlaceholder) => {
          perf.placeholder.value = performance.now() - perf.placeholder.value;
          perf.hash.value = performance.now();
          const sigEmb = await SignatureEmbeder.fromPdfAsync(pdfWithPlaceholder);
          const hashArray = Array.from(new Uint8Array(await self.crypto.subtle.digest(hashAlgorithm, sigEmb.getSignBuffer())));
          const pdfHashValue = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");

          perf.hash.value = performance.now() - perf.hash.value;
          return { pdfWithPlaceholder, pdfHashValue };
        }
      );

      // intentionally using await here to reduce precision errors in perfromance
      // measurements caused by async.
      perf.key.value = performance.now();
      const privateKeyAndCsr = await this.createRsaKeyPair(RSA_KEY_SIZE).then(async (keys) => {
        perf.key.value = performance.now() - perf.key.value;
        perf.csr.value = performance.now();
        const csrPem = await this.createCsr(keys);
        perf.csr.value = performance.now() - perf.csr.value;

        // https://stackoverflow.com/a/55188241
        let privBody = self.btoa(String.fromCharCode(...new Uint8Array(await self.crypto.subtle.exportKey("pkcs8", keys.privateKey))));

        // Nunca será nulo
        // @ts-ignore
        privBody = privBody.match(/.{1,64}/g).join("\n");
        const privatePemRsaKey = `-----BEGIN PRIVATE KEY-----\n${privBody}\n-----END PRIVATE KEY-----`;

        return { csrPem, privatePemRsaKey };
      });

      const [{ csrPem, privatePemRsaKey }, { pdfWithPlaceholder, pdfHashValue }] = [privateKeyAndCsr, placeholderAndHashValuePromise];

      return {
        pdfWithPlaceholder,
        privatePemRsaKey,
        csrPem,
        pdfHashValue,
        perf_results: perf,
      };
    } catch (e) {
      _debugOnConsole("ERROR: %s", e);
      throw e;
    }
  }

  async sign(
    pdf: Buffer,
    privatePemRsaKey: string,
    pkcs7Pem: string,
    hashAlgorithm: string,
    signatureLength: number,
    signingTime: number,
    perf: PerformanceResults
  ): Promise<[Buffer, PerformanceResults, number]> {
    try {
      perf.signature.value = performance.now();
      const pkcs7 = forge.pkcs7.messageFromPem(pkcs7Pem) as forge.pkcs7.PkcsSignedData;

      const privateKey = forge.pki.privateKeyFromPem(privatePemRsaKey);
      const p12Asn1 = forge.asn1.toDer(forge.pkcs12.toPkcs12Asn1(privateKey, pkcs7.certificates, ""));

      // O typescript se confunde com o signatureComputer.
      const signerSettings: any = {
        signatureLength,
        rangePlaceHolder: 10000000000000, // número colocado no ByteRange como placeholder. Colocamos 1tb para garantir que nunca será pequeno demais.
        signatureComputer: {
          certificate: p12Asn1.getBytes(),
          password: "",
        },
      };

      const signer = new PdfDigitalSigner(signerSettings);

      const signingDate = new Date(signingTime);
      const signatureCount = await signer.getSignatureCount(pdf);
      const signParameters: SignDigitalParameters = {
        pageNumber: 1,
        name: `Assinatura ${signatureCount + 1}`, // mesmo nome do `document-signer`
        signature: {
          date: signingDate,
        },
      };

      const signed = await signer.signAsync(pdf, signParameters, false);
      perf.signature.value = performance.now() - perf.signature.value;

      return [signed, perf, signatureCount];
    } catch (e) {
      _debugOnConsole("ERROR: %s", e);
      throw e;
    }
  }

  async verify(
    pdf: Buffer,
    rootCerts: string[],
    OTS_HASH_OID: string[],
    perf: VerificationPerformanceResults
  ): Promise<[SignatureVerifySignatureResult[], VerificationPerformanceResults]> {
    const store = forge.pki.createCaStore(rootCerts);

    // @ts-ignore
    const sigChecker = await new Verifier(
      pdf,
      store,
      async (url) => {
        if (!url.startsWith("http")) {
          _debugOnConsole(`url invalida: ${url}`);
          return null;
        }
        _debugOnConsole("Fetching %s...", url);
        return await fetch(`/api/v1/proxy?url=${encodeURIComponent(url)}`).then(async (res) => {
          if (!res.ok) {
            _debugOnConsole("Error while getting %s: %s (%s)", url, await res.text(), res.status);
            return null;
          }

          return await res.text();
        });
      },
      OTS_HASH_OID,
      perf
    );
    const results = await sigChecker.verifySignaturesAsync();
    _debugOnConsole("RESULTS: ", results);

    return results === undefined ? [[], perf] : [results.signatures as SignatureVerifySignatureResult[], perf];
  }
}
//workerpool.worker({
//  prepareSign: worker.prepareSign.bind(worker),
//  sign: worker.sign.bind(worker),
//  verify: worker.verify.bind(worker),
//});
//
